import { css } from 'styled-components';

import { Variant } from 'design-system/theme';
import PseudoClassProps from 'design-system/types/PseudoClassProps';
import { TransitionDuration } from 'design-system/variables/transitions';

interface MenuItemMixinProps extends PseudoClassProps {
  variant?: Variant;
}

/**
 * Adds styles for hovered and selected states. For use with dropdowns, menu
 * items, etc.
 *
 * The mixins are split by interaction state for more flexibility. We need to
 * implement each state differently when used with React Router's NavLink, for
 * example.
 *
 * Note that we override the palette for 'basic' to match the neutral usecase
 * shown in the mocks. There are never menu items that match our basic palette.
 *
 * Default: standard and disabled styles
 * Hover: hover and focus styles
 * Active: active styles
 */
const menuItemMixins = {
  default: css<MenuItemMixinProps>`
    cursor: pointer;
    color: ${({ theme, variant = 'basic' }) =>
      // default text color for these two should be neutral
      ['basic', 'primary'].includes(variant)
        ? theme.color.text.primary
        : theme.color.variants[variant].default};

    ${({ theme }) => css`
      background-color: ${theme.color.background.primary};
      border-radius: ${theme.element.borderRadius};
    `}

    ${({ theme, disabled }) =>
      disabled &&
      css`
        background-color: ${theme.color.background.primary};
        color: ${theme.color.text.disabled};
        cursor: default;

        svg * {
          stroke: ${theme.color.text.disabled};
        }

        svg.pds-icon {
          * {
            stroke: unset;
          }
          /* Support icons with stroke */
          *[stroke] {
            stroke: currentColor;
          }
        }
      `}
  `,
  hover: css<MenuItemMixinProps>`
    transition-duration: ${TransitionDuration.Fast};
    color: ${({ theme, variant = 'basic' }) =>
      variant === 'basic' ? theme.color.text.primary : theme.color.variants[variant].default};

    background-color: ${({ theme, variant = 'basic' }) =>
      variant === 'basic'
        ? theme.color.background.secondary
        : theme.color.variants[variant].highlightLight};

    ${({ theme, disabled, variant = 'basic' }) =>
      !disabled &&
      css`
        svg * {
          stroke: ${variant === 'basic'
            ? theme.color.text.primary
            : theme.color.variants[variant].default};
        }

        svg.pds-icon {
          * {
            stroke: unset;
          }
          /* Support icons with stroke */
          *[stroke] {
            stroke: currentColor;
          }
        }
      `}
  `,
  active: css<MenuItemMixinProps>`
    transition-duration: ${TransitionDuration.Fast};
    color: ${({ theme, variant = 'basic' }) =>
      variant === 'basic' ? theme.color.text.inverse : theme.color.variants[variant].default};
    background-color: ${({ theme, variant = 'basic' }) =>
      variant === 'basic'
        ? theme.color.primary.brand
        : theme.color.variants[variant].highlightLight};

    ${({ theme, disabled, variant = 'basic' }) =>
      !disabled &&
      css`
        svg * {
          stroke: ${variant === 'basic'
            ? theme.color.text.primary
            : theme.color.variants[variant].default};
        }

        svg.pds-icon {
          * {
            stroke: unset;
          }
          /* Support icons with stroke */
          *[stroke] {
            stroke: currentColor;
          }
        }
      `}
  `,
};

export default menuItemMixins;
